// DesktopTaskbar.jsx — the Win98 taskbar pinned to the bottom of the desktop.
// A *view* of DesktopEnvironment's window state: one button per open window,
// a Start menu for launching programs, and a tray with messenger/donate
// shortcuts and the clock.
import React, { useEffect, useRef, useState } from "react";
import OSIcon32 from "../../icons/OSIcon32";

export default function DesktopTaskbar({
  windows,            // [{ id, label, icon, minimized }] in open order
  activeId,           // id of the focused (top z, visible) window
  onWindowButtonClick,
  startApps,          // [{ id, label, icon }] launchable from the Start menu
  onStartApp,
  links,              // [{ label, href, icon }] external links in the Start menu
  onTrayMessenger,
  onTrayDonate,
  unreadMessengerCount = 0,
  currentTime
}) {
  const [startOpen, setStartOpen] = useState(false);
  const startRef = useRef(null);

  // Close the Start menu on outside click or Escape.
  useEffect(() => {
    if (!startOpen) return;
    const onPointerDown = (e) => {
      if (startRef.current && !startRef.current.contains(e.target)) {
        setStartOpen(false);
      }
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") setStartOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [startOpen]);

  const messengerAriaLabel =
    unreadMessengerCount > 0
      ? `Buddy Messenger (${unreadMessengerCount} new ${unreadMessengerCount === 1 ? "message" : "messages"})`
      : "Buddy Messenger";

  return (
    <div className="win95-taskbar-98" ref={startRef}>
      <button
        type="button"
        className="win95-taskbar-98__start"
        onClick={() => setStartOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={startOpen}
      >
        <img
          src="/assets/gnf-logo.png"
          alt=""
          width="16"
          height="16"
          style={{ display: "block" }}
        />
        Start
      </button>

      {startOpen && (
        <div className="win95-startmenu" role="menu" aria-label="Start menu">
          <div className="win95-startmenu__banner" aria-hidden="true">
            <span>NeighborhoodOS</span>
          </div>
          <div className="win95-startmenu__items">
            {startApps.map((app) => (
              <button
                key={app.id}
                type="button"
                role="menuitem"
                className="win95-startmenu__item"
                onClick={() => {
                  setStartOpen(false);
                  onStartApp(app.id);
                }}
              >
                <OSIcon32 kind={app.icon} size={24} />
                {app.label}
              </button>
            ))}
            <div className="win95-startmenu__divider" aria-hidden="true" />
            {links.map((link) => (
              <a
                key={link.label}
                role="menuitem"
                className="win95-startmenu__item"
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setStartOpen(false)}
              >
                {link.icon}
                {link.label}
              </a>
            ))}
          </div>
        </div>
      )}

      <div className="win95-toolbar__sep" aria-hidden="true" />

      {windows.map((win) => (
        <button
          key={win.id}
          type="button"
          className={`win95-taskbar-btn ${win.id === activeId ? "is-active" : ""}`}
          onClick={() => onWindowButtonClick(win.id)}
          aria-label={win.minimized ? `Restore ${win.label}` : win.label}
        >
          <OSIcon32 kind={win.icon} size={16} style={{ flex: "none" }} />
          <span className="win95-taskbar-btn__label">{win.label}</span>
        </button>
      ))}

      <div style={{ flex: 1 }} />

      <div className="win95-taskbar-98__tray">
        <button
          type="button"
          className="win95-tray-btn"
          onClick={onTrayMessenger}
          aria-label={messengerAriaLabel}
        >
          <OSIcon32 kind="messenger" size={16} />
          {unreadMessengerCount > 0 && (
            <span className="win95-tray-btn__badge" aria-hidden="true">
              {unreadMessengerCount > 99 ? "99+" : unreadMessengerCount}
            </span>
          )}
        </button>
        <button
          type="button"
          className="win95-tray-btn"
          onClick={onTrayDonate}
          aria-label="Donate"
        >
          <OSIcon32 kind="donate" size={16} />
        </button>
        <div className="win95-taskbar-98__tray-divider" aria-hidden="true" />
        <span className="retro-clock">
          {currentTime?.time}
          <span className="retro-clock__date"> · {currentTime?.date}</span>
          <span className="retro-clock__date--compact"> · {currentTime?.dateCompact}</span>
        </span>
      </div>
    </div>
  );
}
